/**
 * MediaSelector — Reusable component for choosing existing media from the media library.
 * Fetches GET /api/v1/media with optional params (folderId, usage, type), displays a grid
 * of thumbnails, allows multi-select, and returns selected items to the parent via onSelect.
 *
 * Usage: Parent renders MediaSelector (e.g. inside a modal); on "Confirm" the parent
 * receives selected items (id, fileUrl) and can save them on the clinic/circle form.
 *
 * @see docs-teleconsultation/AUDIT-MEDIAS-LOGIQUE-ET-OPTIONS.md §3.1
 * @see docs-teleconsultation/SPEC-MEDIA-SELECTOR-INTEGRATION.md
 */

import React, { useState, useEffect, useCallback } from "react";
import http from "../../../utils/http-common";
import { API_BASE_URL } from "../../../utils/Utils";

const DEFAULT_PAGE_SIZE = 24;

const getAuthHeaders = () => {
  const token = typeof window !== "undefined" ? localStorage.getItem("accessToken") : null;
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/** Build full URL for media (thumb/file). Backend returns relative paths like /uploads/media/xxx. */
function getMediaDisplayUrl(path) {
  if (!path || typeof path !== "string") return "";
  const p = path.trim();
  if (p.startsWith("http://") || p.startsWith("https://")) return p;
  const base = (API_BASE_URL || "").replace(/\/$/, "");
  return p.startsWith("/") ? `${base}${p}` : `${base}/${p}`;
}

/**
 * @param {Object} props
 * @param {Function} props.onSelect - (selectedItems: Array<{ id: string, fileUrl: string, thumbnailUrl?: string, title?: string }>) => void
 * @param {Function} [props.onCancel] - () => void when user cancels
 * @param {boolean} [props.multiple=true] - allow selecting multiple medias
 * @param {string[]} [props.initialSelectedIds=[]] - pre-selected media IDs
 * @param {string} [props.folderId] - filter by folder ID
 * @param {string} [props.usage] - filter by usage (e.g. clinic, partner, global, banner)
 * @param {string} [props.type] - filter by type (e.g. image, video, document)
 * @param {number} [props.pageSize=24]
 * @param {string} [props.confirmButtonLabel] - primary button label (default: "Add selected photos"); use e.g. "Add selected images" for before/after
 */
const MediaSelector = ({
  onSelect,
  onCancel,
  multiple = true,
  initialSelectedIds = [],
  folderId,
  usage,
  type,
  pageSize = DEFAULT_PAGE_SIZE,
  confirmButtonLabel = "Add selected photos",
}) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedIds, setSelectedIds] = useState(() => new Set(initialSelectedIds));

  const fetchMedia = useCallback(
    async (pageNum = 1) => {
      setLoading(true);
      setError(null);
      try {
        const params = {
          page: pageNum,
          limit: pageSize,
          ...(folderId && { folderId }),
          ...(usage && { usage }),
          ...(type && { type }),
        };
        const response = await http.get("/api/v1/media", {
          params,
          headers: getAuthHeaders(),
        });
        const data = response.data?.data ?? response.data;
        const list = Array.isArray(data) ? data : data?.items ?? data?.list ?? [];
        const totalCount = response.data?.total ?? response.data?.count ?? list.length;
        setItems(list);
        setTotal(totalCount);
        setTotalPages(Math.max(1, Math.ceil(totalCount / pageSize)));
        setPage(pageNum);
      } catch (err) {
        setError(err.response?.data?.message || err.message || "Failed to load media");
        setItems([]);
      } finally {
        setLoading(false);
      }
    },
    [folderId, usage, type, pageSize]
  );

  useEffect(() => {
    fetchMedia(page);
  }, [fetchMedia, page]);

  const toggleSelection = useCallback(
    (item) => {
      const id = item._id || item.id;
      if (!id) return;
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) {
          next.delete(id);
        } else {
          if (!multiple) next.clear();
          next.add(id);
        }
        return next;
      });
    },
    [multiple]
  );

  const handleConfirm = useCallback(() => {
    const selected = items.filter((item) => selectedIds.has(item._id || item.id));
    const payload = selected.map((item) => ({
      id: item._id || item.id,
      fileUrl: item.fileUrl || item.url || "",
      thumbnailUrl: item.thumbnailUrl || item.thumbUrl,
      title: item.title || item.name,
    }));
    onSelect?.(payload);
  }, [items, selectedIds, onSelect]);

  const isSelected = useCallback(
    (item) => selectedIds.has(item._id || item.id),
    [selectedIds]
  );

  const selectedCount = selectedIds.size;

  if (loading && items.length === 0) {
    return (
      <div className="media-selector media-selector--loading" role="status" aria-label="Loading media">
        <p>Loading media...</p>
      </div>
    );
  }

  return (
    <div className="media-selector" role="dialog" aria-label="Select media from library">
      {error && (
        <div className="media-selector__error" role="alert">
          {error}
        </div>
      )}

      <div className="media-selector__grid" role="list">
        {items.map((item) => {
          const id = item._id || item.id;
          const thumbRaw = item.thumbnailUrl || item.thumbUrl || item.fileUrl || item.url;
          const thumb = getMediaDisplayUrl(thumbRaw);
          const selected = isSelected(item);
          return (
            <button
              key={id}
              type="button"
              className={`media-selector__card ${selected ? "media-selector__card--selected" : ""}`}
              onClick={() => toggleSelection(item)}
              aria-pressed={selected}
              aria-label={item.title ? `Select ${item.title}` : `Select media ${id}`}
            >
              <span className="media-selector__card-inner">
                {thumb ? (
                  <img
                    src={thumb}
                    alt={item.alt || item.title || ""}
                    className="media-selector__thumb"
                  />
                ) : (
                  <span className="media-selector__thumb media-selector__thumb--placeholder">
                    No preview
                  </span>
                )}
                {selected && (
                  <span className="media-selector__check" aria-hidden="true">
                    ✓
                  </span>
                )}
              </span>
              {item.title && (
                <span className="media-selector__title">{item.title}</span>
              )}
            </button>
          );
        })}
      </div>

      {totalPages > 1 && (
        <div className="media-selector__pagination">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            aria-label="Previous page"
          >
            Previous
          </button>
          <span>
            Page {page} of {totalPages} ({total} total)
          </span>
          <button
            type="button"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            aria-label="Next page"
          >
            Next
          </button>
        </div>
      )}

      <div className="media-selector__actions">
        {onCancel && (
          <button type="button" className="media-selector__btn media-selector__btn--secondary" onClick={onCancel}>
            Cancel
          </button>
        )}
        <button
          type="button"
          className="media-selector__btn media-selector__btn--primary"
          onClick={handleConfirm}
          disabled={selectedCount === 0}
          aria-label={selectedCount ? `${confirmButtonLabel} (${selectedCount})` : "Select at least one media"}
        >
          {confirmButtonLabel}{selectedCount > 0 ? ` (${selectedCount})` : ""}
        </button>
      </div>

      <style>{`
        .media-selector { padding: 16px; max-width: 100%; }
        .media-selector--loading { min-height: 120px; display: flex; align-items: center; justify-content: center; }
        .media-selector__error { color: #c00; margin-bottom: 12px; }
        .media-selector__grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(120px, 1fr)); gap: 12px; margin-bottom: 16px; max-height: 360px; overflow-y: auto; }
        .media-selector__card { border: 2px solid #e2e8f0; border-radius: 8px; padding: 8px; background: #fff; cursor: pointer; text-align: left; transition: border-color .15s; }
        .media-selector__card:hover { border-color: #94a3b8; }
        .media-selector__card--selected { border-color: #0ea5e9; background: #f0f9ff; }
        .media-selector__card-inner { position: relative; display: block; aspect-ratio: 1; overflow: hidden; border-radius: 4px; }
        .media-selector__thumb { width: 100%; height: 100%; object-fit: cover; display: block; }
        .media-selector__thumb--placeholder { display: flex; align-items: center; justify-content: center; background: #e2e8f0; font-size: 11px; color: #64748b; }
        .media-selector__check { position: absolute; top: 4px; right: 4px; background: #0ea5e9; color: #fff; width: 22px; height: 22px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: bold; }
        .media-selector__title { display: block; margin-top: 6px; font-size: 12px; color: #334155; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .media-selector__pagination { display: flex; align-items: center; gap: 12px; margin-bottom: 16px; flex-wrap: wrap; }
        .media-selector__pagination button { padding: 6px 12px; cursor: pointer; }
        .media-selector__pagination button:disabled { opacity: .5; cursor: not-allowed; }
        .media-selector__actions { display: flex; gap: 12px; justify-content: flex-end; }
        .media-selector__btn { padding: 8px 16px; border-radius: 6px; cursor: pointer; font-size: 14px; }
        .media-selector__btn--primary { background: #0ea5e9; color: #fff; border: none; }
        .media-selector__btn--primary:disabled { background: #94a3b8; cursor: not-allowed; }
        .media-selector__btn--secondary { background: #e2e8f0; color: #334155; border: 1px solid #cbd5e1; }
      `}</style>
    </div>
  );
};

export default MediaSelector;
